import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';

// ── Drip Sequences (/api/drip-sequences) — estimate follow-up cadence ────

export interface DripSequence {
  id: string;
  campaign_id: string;
  phone: string;
  name?: string | null;
  anchor_date: string;
  day_offsets: number[];
  next_offset_index: number;
  status: 'active' | 'completed' | 'stopped_signed' | 'stopped_optout' | 'stopped_declined' | 'stopped_expired' | 'stopped_other';
  stop_reason?: string | null;
  profile?: Record<string, unknown> | null;
  estimate_id?: string | null;
  created_at: string;
  updated_at: string;
  last_touch_at?: string | null;
}

export type CreateDripSequenceInput = {
  campaign_id: string;
  phone: string;
  name?: string;
  anchor_date: string;
  day_offsets?: number[];
  profile?: Record<string, unknown>;
  estimate_id?: string;
};

export type DripSequenceLogEntry = {
  id: string;
  event_type: 'touch_created' | 'auto_stopped';
  day_offset?: number | null;
  touch_number?: number | null;
  touch_tone?: string | null;
  stop_reason?: string | null;
  scheduled_campaign_id?: string | null;
  sms_sent: boolean;
  created_at: string;
};

export async function fetchDripSequences(campaignId: string, axiosInstance?: AxiosInstance): Promise<DripSequence[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<DripSequence[]>(apiConfig.voiceEndpoints.dripSequences, { params: { campaign_id: campaignId } });
  return response.data;
}

export async function createDripSequence(input: CreateDripSequenceInput, axiosInstance?: AxiosInstance): Promise<DripSequence> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<DripSequence>(apiConfig.voiceEndpoints.dripSequences, input);
  return response.data;
}

export async function stopDripSequence(id: string, reason: 'signed' | 'opted_out' | 'other', axiosInstance?: AxiosInstance): Promise<DripSequence> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<DripSequence>(`${apiConfig.voiceEndpoints.dripSequences}/${id}/stop`, { reason });
  return response.data;
}

export async function fastForwardDripSequence(id: string, axiosInstance?: AxiosInstance): Promise<DripSequence> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<DripSequence>(`${apiConfig.voiceEndpoints.dripSequences}/${id}/fast-forward`, {});
  return response.data;
}

export async function fetchDripSequenceLog(id: string, axiosInstance?: AxiosInstance): Promise<DripSequenceLogEntry[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<DripSequenceLogEntry[]>(`${apiConfig.voiceEndpoints.dripSequences}/${id}/log`);
  return response.data;
}
